<template>
  <div class="relative text-yapgray-500">
    <label
      v-if="!noLabel && !isFloatingLabel"
      :for="id ? id : name"
      class="left-2 z-20 text-xs text-yapgray-600 dark:text-yapgray-400"
      :class="{ 'required-label': !!required }"
    >
      {{ label ? label : schema.title }}
    </label>
    <div class="relative flex w-full items-center">
      <YapIcon v-if="leftIcon" :name="leftIcon" class="pointer-events-none absolute z-20 ml-3 h-5 w-5" />
      <input
        v-model="maskedValue"
        v-maska:[maskOptions]="bindedObject"
        :data-maska="mask"
        :id="id ? id : name"
        :type="type"
        :name="name"
        :maxlength="schema.maxLength"
        :minlength="schema.minLength"
        :min="min || schema.minimum"
        :max="max || schema.maximum"
        :required="required"
        :placeholder="internalPlaceholder"
        :readonly="readonly || schema.readonly"
        v-bind="$attrs"
        :accept="accept"
        :autocomplete="autocomplete"
        class="peer w-full overflow-visible border-yapgray-300 text-yapgray-800 focus:z-10 focus:border-primary-500 dark:focus:border-primary-dark-400 focus:ring-primary-500 dark:border-yapdarkgray-600 dark:focus:ring-primary-dark-400 dark:bg-yapdarkgray-800 dark:text-yapgray-100"
        :class="{
          'bg-yapgray-100 text-yapgray-600 dark:bg-yapdarkgray-900 dark:text-yapgray-200': readonly || schema.readonly,
          'rounded-lg shadow-sm': !grouped,
          'border-0': grouped && !error,
          'h-14': floating,
          'border-yapred-500 focus:border-yapred-500 focus:ring-yapred-500 dark:border-yapred-500 dark:focus:border-yapred-500 dark:focus:ring-yapred-500':
            error,
          'pl-10': !!leftIcon,
          'pt-3.5 placeholder-transparent dark:placeholder-transparent': floating,
          'dark:placeholder-yapdarkgray-300': !floating
        }"
        :data-testid="`yap-input-${id ? id : name}`"
        @input="updateValue"
        @blur="$emit('blur')"
      />
      <div class="absolute right-2 ml-3 flex items-center">
        <YapIcon v-if="rightIcon" :name="rightIcon" class="pointer-events-none absolute z-20 -ml-5 w-5" />
        <YapTooltip :text="hint" class="w-56" v-if="!!hint">
          <YapIcon
            name="info-outline"
            class="w-5 cursor-pointer text-yapgray-500 hover:text-primary-500 dark:hover:text-primary-dark-300"
            @click="resetInput"
          />
        </YapTooltip>
        <YapIcon
          v-if="clearable && maskedValue !== undefined && maskedValue !== ''"
          name="close-outline"
          class="z-30 w-5 cursor-pointer hover:text-primary-500 dark:hover:text-primary-dark-300"
          @click="resetInput"
        />
      </div>
      <label
        v-if="!noLabel && isFloatingLabel"
        :for="id ? id : name"
        class="absolute top-0.5 z-20 block w-full text-xs text-yapgray-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:text-yapgray-400 dark:peer-placeholder-shown:text-yapgray-600 peer-focus:top-0.5 peer-focus:text-xs peer-focus:text-primary-500 dark:peer-focus:text-primary-dark-300"
        :class="[!!leftIcon ? 'pl-10' : 'left-2']"
      >
        <span
          class="w-full pb-1 pr-4"
          :class="{
            'text-yapgray-600 dark:text-yapgray-200': readonly || schema.readonly,
            'required-label': !!required
          }"
          >{{ label ? label : schema.title }}</span
        >
      </label>
      <div v-if="isFloatingLabel" class="absolute bottom-0 z-20">
        <YapInputError
          v-if="floating"
          :error="error"
          class="xmt-0 z-20 text-sm"
          :class="[!!leftIcon ? 'pl-10' : 'pl-2']"
        />
      </div>
    </div>
    <YapCharCounter
      v-if="showCharacterCounter && schema.maxLength"
      :current-length="currentLength"
      :max-length="schema.maxLength"
    />
    <YapInputError v-if="!isFloatingLabel" :error="error" class="mt-0" />
  </div>
</template>

<script setup lang="ts">
// Documentation on how to use maska: https://beholdr.github.io/maska/#/
import { MaskInputOptions, MaskaDetail, vMaska } from 'maska';
import { ref, computed, reactive, watch, onMounted } from 'vue';

import YapIcon from '@/components/YapIcon.vue';
import YapTooltip from '@/components/YapTooltip.vue';
import { SchemaItem } from '@/components/form/types/schemaItem';
import YapInputError from '@/components/form/YapInputError.vue';
import useInputValidation from '@/composables/useInputValidation';
import { YapInputValidationError } from '@/components/form/types/yapInputValidationError';
import YapCharCounter from '@/components/form/YapCharCounter.vue';

const emit = defineEmits(['update:modelValue', 'blur']);

const props = defineProps<{
  floating?: boolean;
  label?: string;
  noLabel?: boolean;
  required?: boolean;
  name: string;
  id?: string;
  schema: SchemaItem;
  accept?: string;
  errors?: Array<YapInputValidationError>;
  boldLabel?: boolean;
  inputType?: 'text' | 'textarea';
  min?: string | number;
  max?: string | number;
  placeholder?: string;
  leftIcon?: string;
  rightIcon?: string;
  grouped?: boolean;
  clearable?: boolean;
  hint?: string;
  readonly?: boolean; // Allows to force readonly and bypass what's defined in the schema
  showCounter?: boolean;
  mask?: string;
  maskOptions?: MaskInputOptions;
  unmaskedModel?: boolean;
  autocomplete?: string;
}>();

const modelValue = defineModel<string>();

const maskedValue = ref(modelValue.value);
const bindedObject: MaskaDetail = reactive({ masked: '', unmasked: '', completed: false });

const showCharacterCounter = computed(() => props.showCounter ?? false);
const currentLength = computed(() => maskedValue.value?.length ?? 0);

interface State {
  localError?: YapInputValidationError;
  error?: YapInputValidationError;
}

const { filterErrorFor, validateSchemaPattern } = useInputValidation();
const state: State = reactive({
  localError: undefined,
  error: props.errors ? filterErrorFor(props.name, props.errors) : undefined
});

function resetInput() {
  modelValue.value = '';
  maskedValue.value = '';
  emit('update:modelValue', '');
}

const error = computed(() => {
  if (!!state.error) {
    return state.error;
  } else if (!!state.localError) {
    return state.localError;
  }
  return undefined;
});

watch(
  () => props.errors,
  (newVal, oldVal) => {
    if (newVal) {
      state.error = filterErrorFor(props.name, newVal);
    }
  },
  { deep: true }
);

onMounted(async () => {
  if (props.schema.default) {
    modelValue.value = typeof props.schema.default === 'string' ? props.schema.default : String(props.schema.default);
    maskedValue.value = modelValue.value;
  }
});

/**
 * Regardless of floating props, in case of switch we do not want any floating label
 */
const isFloatingLabel = computed(
  () => props.floating && type.value != 'boolean' && typeof modelValue.value !== 'boolean'
);

const type = computed(() => {
  switch (props.schema.type) {
    case 'string':
      if (props.schema.format === 'date') {
        return 'date';
      } else if (props.schema.format === 'email') {
        return 'email';
      } else {
        return 'text';
      }
    case 'number':
    case 'boolean':
    case 'array':
      return props.schema.type;

    default:
      return 'text';
  }
});

const internalPlaceholder = computed(() =>
  props.placeholder ? props.placeholder : props.floating ? 'placeholder' : undefined
);

watch(
  () => modelValue.value,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      maskedValue.value = newVal;
    }
  }
);

function updateValue(event: Event) {
  if (props.unmaskedModel) {
    state.error = undefined;
    emit('update:modelValue', bindedObject.unmasked);
  } else {
    emit('update:modelValue', bindedObject.masked);
  }
}
</script>
<style scoped>
.required-label:after {
  @apply ml-0.5 text-yapred-500;
  content: '*';
}
</style>
